/**
 * The board's keys (ADR 0194, decision 4), as the owner's prototype has them
 * ("Space on the card under the mouse, arrows between cards"):
 *
 * - **Space acts on whichever was used last.** After the keyboard moved the
 *   focus (the arrows, Tab, Home or End), it's the focused card. Once the
 *   mouse really moves, it's the card under the mouse. A pointer event whose
 *   position hasn't changed doesn't count: the browser sends those as cards
 *   grow, or the page scrolls, under a still mouse.
 * - **The arrow keys move between cards,** with focus on a card or on nothing
 *   in particular: ↑ and ↓ within the column; ← and → to the neighbouring
 *   column's card whose middle is nearest this one's; Home and End to the
 *   column's first and last card. With nothing focused, the first press
 *   starts at the card under the mouse, or else the first card.
 *
 * - **Selecting several cards** (decision 5), when the board takes it
 *   (`registerSelection`; never on a phone): Ctrl+Space (⌘ on a Mac) on the
 *   focused card selects it or unselects it; Shift+↑ ↓ moves to the card
 *   above or below and selects the range from the anchor; Esc, on a card or
 *   on nothing in particular, clears the selection, before the open-tickets
 *   tray takes it.
 *
 * Never in a field, a menu, a popup or another control: there, the keys are
 * that control's. Nor with Alt.
 *
 * Each card's peek (`usePeek`) registers its button here while it's on the
 * page. The window's listeners are here, once, while any card is.
 */

/** What Space does to a card, wherever the focus is. */
export interface SpaceControls {
  /** It peeks here: not on a phone, where Space is the button's own. */
  available: () => boolean;
  /** Space pressed: it dips at once, and peeks if it's held. */
  down: () => void;
  /** Space let go: a tap opens it, and a held one folds. */
  up: () => void;
}

/**
 * Selecting several cards (ADR 0194, decision 5): the board's, while it's on
 * the page. Each says whether it did something, so the key is the board's.
 */
export interface BoardSelection {
  /** Ctrl+Space on a card: it's selected, or unselected. */
  toggle: (card: HTMLElement) => boolean;
  /** Shift+↑ ↓ from `from` to `to`, the card above or below it: the range from the anchor. */
  extend: (from: HTMLElement, to: HTMLElement) => boolean;
  /** Esc: the selection cleared; false when nothing was selected. */
  clear: () => boolean;
}

let selection: BoardSelection | null = null;

/** The board's selecting several, until it goes. */
export function registerSelection(handlers: BoardSelection): () => void {
  selection = handlers;
  return () => {
    if (selection === handlers) {
      selection = null;
    }
  };
}

/** The keys that move the focus between cards. */
const NAV_KEYS: ReadonlySet<string> = new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End"]);
/** The keys that say the keyboard moved the focus last. */
const KEYBOARD_KEYS: ReadonlySet<string> = new Set(["Tab", ...NAV_KEYS]);
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const cards = new Map<HTMLElement, SpaceControls>();

/**
 * What was used last, in the order it happened: a count, not a clock, so two
 * events in the same millisecond still have an order.
 */
let clock = 0;
let keysAt = 0;
const mouse = { x: Number.NaN, y: Number.NaN, inside: false, movedAt: 0, over: null as Element | null };
/** The card Space is held on, to let go of wherever the focus is by then. */
let held: SpaceControls | null = null;

/** A card's button that can take the keys: not behind a popup. */
function reachable(button: HTMLElement): boolean {
  return button.isConnected && button.closest("[inert], [aria-hidden='true']") === null;
}

/** The card an element is part of: its button, or what its card holds (the chip). */
function cardAt(element: Element | null): HTMLElement | null {
  if (element === null) {
    return null;
  }
  for (const button of cards.keys()) {
    if ((button.closest("li") ?? button).contains(element)) {
      return reachable(button) ? button : null;
    }
  }
  return null;
}

/**
 * The card under the mouse. For Space, only if the mouse moved since the
 * keyboard last moved the focus; for an arrow from nowhere (`anyMove`),
 * either way. Cards move under a still mouse, so it's what's there now.
 */
function hovered(anyMove: boolean): HTMLElement | null {
  if (!mouse.inside || (!anyMove && keysAt > mouse.movedAt)) {
    return null;
  }
  // A browser says what's there; the tests' page can't, and goes by the last event's target.
  const at = typeof document.elementFromPoint === "function" ? document.elementFromPoint(mouse.x, mouse.y) : mouse.over;
  return cardAt(at);
}

/** The focused card; null with focus on nothing in particular; undefined on anything else. */
function focusedCard(target: EventTarget | null): HTMLElement | null | undefined {
  if (target === document.body || target === document.documentElement || target === null) {
    return null;
  }
  return target instanceof HTMLElement && cards.has(target) ? target : undefined;
}

/** The board's columns that have cards, each its cards in the page's order. */
function columns(): HTMLElement[][] {
  const ordered = [...cards.keys()]
    .filter(reachable)
    .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
  const byColumn = new Map<Element, HTMLElement[]>();
  for (const button of ordered) {
    const column = button.closest("[data-column]");
    if (column !== null) {
      byColumn.set(column, [...(byColumn.get(column) ?? []), button]);
    }
  }
  return [...byColumn.values()];
}

function middle(button: HTMLElement): number {
  const box = button.getBoundingClientRect();
  return box.top + box.height / 2;
}

/**
 * Where a key goes from a card, or from nowhere (null): to the card under the
 * mouse, or the first card. null: nowhere further that way. undefined: there
 * are no cards.
 */
function destination(from: HTMLElement | null, key: string): HTMLElement | null | undefined {
  const all = columns();
  const [first] = all;
  if (first === undefined) {
    return undefined;
  }
  if (from === null) {
    return hovered(true) ?? first[0] ?? null;
  }
  const at = all.findIndex((column) => column.includes(from));
  const column = all[at];
  if (column === undefined) {
    return null;
  }
  const i = column.indexOf(from);
  switch (key) {
    case "ArrowUp":
      return column[i - 1] ?? null;
    case "ArrowDown":
      return column[i + 1] ?? null;
    case "Home":
      return column[0] ?? null;
    case "End":
      return column[column.length - 1] ?? null;
  }
  // ← →: the neighbouring column's card whose middle is nearest this one's.
  const next = all[at + (key === "ArrowLeft" ? -1 : 1)];
  if (next === undefined) {
    return null;
  }
  const y = middle(from);
  let nearest: HTMLElement | null = null;
  for (const button of next) {
    if (nearest === null || Math.abs(middle(button) - y) < Math.abs(middle(nearest) - y)) {
      nearest = button;
    }
  }
  return nearest;
}

/** Focus on the card, scrolled into view if it isn't. */
function moveTo(button: HTMLElement) {
  button.focus({ preventScroll: true });
  const box = button.getBoundingClientRect();
  if ((box.top < 12 || box.bottom > window.innerHeight - 12) && typeof button.scrollIntoView === "function") {
    const reduced = typeof window.matchMedia === "function" && window.matchMedia(REDUCED_MOTION_QUERY).matches;
    button.scrollIntoView({ block: "nearest", behavior: reduced ? "auto" : "smooth" });
  }
}

function onPointer(event: PointerEvent) {
  if (event.pointerType === "touch") {
    return;
  }
  if (event.clientX !== mouse.x || event.clientY !== mouse.y) {
    clock += 1;
    mouse.movedAt = clock;
  }
  mouse.x = event.clientX;
  mouse.y = event.clientY;
  mouse.inside = true;
  mouse.over = event.target instanceof Element ? event.target : null;
}

function onPointerOut(event: PointerEvent) {
  // Off the page altogether.
  if (event.pointerType !== "touch" && event.relatedTarget === null) {
    mouse.inside = false;
    mouse.over = null;
  }
}

/** A popup's keys are its own, wherever its focus is. */
function popupOpen(): boolean {
  return document.querySelector('[role="dialog"][aria-modal="true"]') !== null;
}

/** First, before anything can stop it: the cards' own handlers do. */
function onKeyFirst(event: KeyboardEvent) {
  if (KEYBOARD_KEYS.has(event.key)) {
    clock += 1;
    keysAt = clock;
  }
  // Esc clears the selection first, before the open-tickets tray (which
  // listens on the document) takes it: from a card, or from nothing.
  if (
    event.key === "Escape" &&
    selection !== null &&
    !event.defaultPrevented &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.shiftKey &&
    focusedCard(event.target) !== undefined &&
    !popupOpen() &&
    selection.clear()
  ) {
    event.preventDefault();
  }
}

function onKeyDown(event: KeyboardEvent) {
  if (event.defaultPrevented || event.altKey || popupOpen()) {
    return;
  }
  const focused = focusedCard(event.target);
  if (focused === undefined) {
    return;
  }
  if (event.ctrlKey || event.metaKey) {
    // Ctrl+Space (⌘ on a Mac): the focused card selected, or unselected.
    if (event.key === " " && !event.shiftKey && focused !== null && held === null && selection?.toggle(focused) === true) {
      event.preventDefault();
    }
    return;
  }
  // Shift+↑ ↓: on to the card above or below, the range from the anchor selected.
  if (event.shiftKey && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
    if (focused === null || held !== null || selection === null) {
      return;
    }
    event.preventDefault();
    const to = destination(focused, event.key);
    if (to !== null && to !== undefined && selection.extend(focused, to)) {
      moveTo(to);
    }
    return;
  }
  if (event.key === " ") {
    if (event.shiftKey) {
      return;
    }
    const target = hovered(false) ?? focused;
    const controls = target === null ? undefined : cards.get(target);
    if (controls === undefined || !controls.available()) {
      return;
    }
    // A button's own Space would open it as it's let go, however long it was held.
    event.preventDefault();
    if (event.repeat || held !== null) {
      return;
    }
    held = controls;
    controls.down();
    return;
  }
  if (!NAV_KEYS.has(event.key) || event.shiftKey || held !== null) {
    return;
  }
  const to = destination(focused, event.key);
  if (to === undefined) {
    return;
  }
  event.preventDefault();
  if (to !== null) {
    moveTo(to);
  }
}

function onKeyUp(event: KeyboardEvent) {
  if (event.key !== " " || held === null) {
    return;
  }
  event.preventDefault();
  const controls = held;
  held = null;
  controls.up();
}

/** The window lost focus: Space's keyup won't come. The card's own press folds it (`usePeek`). */
function onBlur() {
  held = null;
}

function listen(on: boolean) {
  const change = on ? window.addEventListener.bind(window) : window.removeEventListener.bind(window);
  change("keydown", onKeyFirst, true);
  change("keydown", onKeyDown);
  change("keyup", onKeyUp);
  change("pointermove", onPointer);
  change("pointerover", onPointer);
  change("pointerdown", onPointer);
  change("pointerout", onPointerOut);
  change("blur", onBlur);
}

/** A card's button, on the page: the board's keys reach it until it goes. */
export function registerCard(button: HTMLElement, controls: SpaceControls): () => void {
  if (cards.size === 0) {
    listen(true);
  }
  cards.set(button, controls);
  return () => {
    if (cards.get(button) !== controls) {
      return;
    }
    cards.delete(button);
    if (held === controls) {
      held = null;
    }
    if (cards.size === 0) {
      listen(false);
      // A new board starts afresh: nothing used yet.
      clock = 0;
      keysAt = 0;
      Object.assign(mouse, { x: Number.NaN, y: Number.NaN, inside: false, movedAt: 0, over: null });
      held = null;
    }
  };
}

/** What the board's description says, for a screen reader. */
export const BOARD_KEYS_HINT =
  "The arrow keys move between cards: up and down within a column, left and right to the next column. " +
  "Home and End go to a column's first and last card. " +
  "Control and Space selects a card, Shift with up or down selects a range, and Escape clears the selection. " +
  "The context menu key, or Shift and F10, opens a card's menu.";
